import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useThemeContext } from "../../context/ThemeProvider";
import Spinner from "../../AppPages/util/Spinner";

export const MANUAL_ACTIVITY_ORIGINS = {
  CHALLENGE_DASHBOARD: "challenge_dashboard",
  STREAKS_PAGE: "streaks_page",
};

export const MANUAL_ACTIVITY_MODES = {
  DISTANCE: "distance",
  STEPS: "steps",
  TIME: "time",
};

export const STEPS_PER_KM = 1250;
const WALKING_SPEED_KMH = 4.8;

export function getStepsDistanceKm(steps) {
  return Number(steps || 0) / STEPS_PER_KM;
}

export function getEstimatedWalkingMinutes(distanceKm) {
  return Math.max(1, Math.round((Number(distanceKm || 0) / WALKING_SPEED_KMH) * 60));
}

function formatDistanceKm(distanceKm) {
  return Number(distanceKm || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatSteps(steps) {
  return Number(steps || 0).toLocaleString();
}

function getDurationPartsFromMinutes(totalMinutes) {
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
    seconds: 0,
  };
}

function getTotalMinutes(hours, minutes, seconds) {
  return Number(hours || 0) * 60 + Number(minutes || 0) + Number(seconds || 0) / 60;
}

function makeSubmissionId() {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getChallengeDateBounds(challenge) {
  const startMs = new Date(challenge?.raceInfo?.raceDateStart).getTime();
  const endMs = new Date(challenge?.raceInfo?.raceDateEnd).getTime();
  return { startMs, endMs };
}

function getChallengeDayKey(value) {
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) {
      return match[1];
    }
  }
  return "";
}

export function isChallengeEligibleForManualDate(
  challenge,
  dateString,
  nowMs = Date.now(),
  timeZone = ""
) {
  if (!challenge?.legId || challenge?.completedDate || !dateString) {
    return false;
  }
  const { startMs, endMs } = getChallengeDateBounds(challenge);
  const activityMs = new Date(`${dateString}T12:00:00`).getTime();
  if (![startMs, endMs, activityMs].every(Number.isFinite)) {
    return false;
  }
  const localDatePolicy = challenge?.raceInfo?.activityDatePolicy === "participant_local_v1";
  const startDayKey = localDatePolicy
    ? challenge?.raceInfo?.activityStartDate
    : getChallengeDayKey(challenge?.raceInfo?.raceDateStart);
  const endDayKey = localDatePolicy
    ? challenge?.raceInfo?.activityEndDate
    : getChallengeDayKey(challenge?.raceInfo?.raceDateEnd);
  const isActivityDateEligible = startDayKey && endDayKey
    ? startDayKey <= dateString && dateString <= endDayKey
    : startMs <= activityMs && activityMs <= endMs;
  let isCurrentlyRunning = startMs <= nowMs && nowMs <= endMs;
  if (localDatePolicy) {
    let todayKey;
    try {
      todayKey = new Intl.DateTimeFormat("en-CA", {
        ...(timeZone ? {timeZone} : {}),
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(nowMs));
    } catch (_error) {
      todayKey = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(nowMs));
    }
    isCurrentlyRunning = Boolean(startDayKey && endDayKey && startDayKey <= todayKey && todayKey <= endDayKey);
  }
  return isCurrentlyRunning && isActivityDateEligible;
}

function getChallengeLabel(challenge) {
  const name = String(challenge?.raceInfo?.name || "").trim();
  const legName = String(challenge?.raceInfo?.legName || "").trim();
  if (name && legName && name !== legName) {
    return `${name} — ${legName}`;
  }
  return name || legName || "Challenge";
}

const initialFields = {
  activityName: "",
  distance: "",
  steps: "",
  hours: "0",
  minutes: "0",
  seconds: "0",
};

export function ManualActivityForm({
  inputMinDateString,
  inputMaxDateString,
  inputDefaultValueString,
  submissionState = "idle",
  submitError = "",
  clearSubmitError,
  showActivityForm,
  closeActivityForm,
  onSubmitActivity,
  showActivityName = false,
  requireActivityName = false,
  showTimeMode = false,
  allowTimeOnlyInDistanceMode = false,
  showChallengeSelection = false,
  fixedChallengeIds = [],
  challenges = [],
  timeZone = "",
  origin,
  title = "Log Activity",
  subtitle = "",
  successMessage = "Your activity has been submitted!",
}) {
  const { colors } = useThemeContext();
  const [error, setError] = useState("");
  const [entryMode, setEntryMode] = useState(MANUAL_ACTIVITY_MODES.DISTANCE);
  const [activityDate, setActivityDate] = useState(inputDefaultValueString || "");
  const [fields, setFields] = useState(initialFields);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [selectedChallengeIds, setSelectedChallengeIds] = useState([]);
  const [hasAttemptedSaveWithoutChallenge, setHasAttemptedSaveWithoutChallenge] = useState(false);

  useEffect(() => {
    if (!showActivityForm) {
      setError("");
      setEntryMode(MANUAL_ACTIVITY_MODES.DISTANCE);
      setActivityDate(inputDefaultValueString || "");
      setFields(initialFields);
      setShowConfirmation(false);
      setPendingData(null);
      setSelectedChallengeIds([]);
      setHasAttemptedSaveWithoutChallenge(false);
    }
  }, [inputDefaultValueString, showActivityForm]);

  const eligibleChallenges = useMemo(
    () => challenges.filter((challenge) =>
      isChallengeEligibleForManualDate(challenge, activityDate, Date.now(), timeZone)
    ),
    [activityDate, challenges, timeZone]
  );

  function clearErrors() {
    setError("");
    setHasAttemptedSaveWithoutChallenge(false);
    clearSubmitError?.();
  }

  function updateField(key, value) {
    setFields((current) => ({ ...current, [key]: value }));
    clearErrors();
  }

  function changeMode(nextMode) {
    setEntryMode(nextMode);
    setShowConfirmation(false);
    setPendingData(null);
    setSelectedChallengeIds([]);
    clearErrors();
  }

  function handleReview(event) {
    event.preventDefault();
    clearErrors();

    const activityName = entryMode === MANUAL_ACTIVITY_MODES.STEPS && showActivityName
      ? "Daily steps"
      : fields.activityName.trim();
    if (requireActivityName && entryMode !== MANUAL_ACTIVITY_MODES.STEPS && !activityName) {
      setError("Add a short activity name before continuing.");
      return;
    }
    if (!activityDate) {
      setError("Choose the activity date before continuing.");
      return;
    }

    let distanceKm = 0;
    let steps = 0;
    let hours = Math.max(0, parseInt(fields.hours, 10) || 0);
    let minutes = Math.max(0, parseInt(fields.minutes, 10) || 0);
    let seconds = Math.max(0, parseInt(fields.seconds, 10) || 0);

    if (entryMode === MANUAL_ACTIVITY_MODES.STEPS) {
      steps = Number(fields.steps);
      if (!Number.isInteger(steps) || steps <= 0) {
        setError("Enter a whole number of steps greater than 0.");
        return;
      }
      distanceKm = getStepsDistanceKm(steps);
      if (distanceKm >= 200) {
        setError("Each step entry can only count for up to 200 km.");
        return;
      }
      const duration = getDurationPartsFromMinutes(getEstimatedWalkingMinutes(distanceKm));
      hours = duration.hours;
      minutes = duration.minutes;
      seconds = duration.seconds;
    } else if (entryMode === MANUAL_ACTIVITY_MODES.DISTANCE) {
      distanceKm = Number(fields.distance);
      if (!Number.isFinite(distanceKm) || distanceKm < 0) {
        setError("Enter a valid distance.");
        return;
      }
      if (!allowTimeOnlyInDistanceMode && distanceKm <= 0) {
        setError("Enter a distance greater than 0.");
        return;
      }
      if (distanceKm >= 200) {
        setError("Each activity can only be up to 200 km long.");
        return;
      }
      if (getTotalMinutes(hours, minutes, seconds) <= 0) {
        setError("Your duration can't be 0.");
        return;
      }
    } else if (getTotalMinutes(hours, minutes, seconds) <= 0) {
      setError("Your duration can't be 0.");
      return;
    }

    const eligibleIds = new Set(eligibleChallenges.map((challenge) => challenge.legId));
    const nextSelectedIds = selectedChallengeIds.filter((legId) => eligibleIds.has(legId));
    setSelectedChallengeIds(nextSelectedIds);
    setPendingData({
      mode: entryMode,
      entryMode,
      date: activityDate,
      activityName,
      distance: Number(distanceKm.toFixed(2)),
      distanceKm: Number(distanceKm.toFixed(2)),
      steps,
      hours,
      minutes,
      seconds,
      totalMinutes: getTotalMinutes(hours, minutes, seconds),
      label: entryMode === MANUAL_ACTIVITY_MODES.STEPS
        ? "manual_steps"
        : origin === MANUAL_ACTIVITY_ORIGINS.CHALLENGE_DASHBOARD
          ? "manual_race"
          : "manual_streak",
      origin,
      clientSubmissionId: makeSubmissionId(),
    });
    setShowConfirmation(true);
  }

  function submitPendingActivity() {
    if (!pendingData || submissionState !== "idle") {
      return;
    }
    if (shouldWarnWithoutChallenge && !hasAttemptedSaveWithoutChallenge) {
      setHasAttemptedSaveWithoutChallenge(true);
      return;
    }
    const challengeLegIds = Array.from(new Set([...fixedChallengeIds, ...selectedChallengeIds])).filter(Boolean);
    Promise.resolve(onSubmitActivity?.({ ...pendingData, challengeLegIds })).catch(() => {});
  }

  function toggleChallenge(legId) {
    setSelectedChallengeIds((current) =>
      current.includes(legId) ? current.filter((value) => value !== legId) : [...current, legId]
    );
    clearErrors();
  }

  if (submissionState === "submitting" || submissionState === "success") {
    const isSuccess = submissionState === "success";
    return (
      <div className="anim-card m-auto w-[min(calc(100vw-2rem),28rem)] rounded-3xl bg-white p-4 shadow-2xl relative">
        {!isSuccess && <Spinner size={48} />}
        {isSuccess && (
          <button
            type="button"
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 text-lg font-bold"
            onClick={closeActivityForm}
            aria-label="Close activity form"
          >
            ✕
          </button>
        )}
        <h6 className={`mb-3 mt-3 text-center ${isSuccess ? "text-green-700" : "text-slate-700"}`}>
          {isSuccess ? successMessage : "Submitting activity..."}
        </h6>
        {!isSuccess && <p className="mb-0 text-center text-sm text-slate-500">Please wait and do not close this page.</p>}
      </div>
    );
  }

  const activeError = error || submitError;
  const inputClassName =
    "!h-12 !rounded-xl !border !border-slate-300 !bg-white !px-3 !text-base !text-slate-900 !shadow-none placeholder:!text-slate-400 focus:!border-slate-500 focus:!ring-0";
  const modeOptions = [
    { key: MANUAL_ACTIVITY_MODES.DISTANCE, label: "Distance" },
    { key: MANUAL_ACTIVITY_MODES.STEPS, label: "Steps" },
    ...(showTimeMode ? [{ key: MANUAL_ACTIVITY_MODES.TIME, label: "Time" }] : []),
  ];
  const shouldShowChallenges =
    showChallengeSelection && showConfirmation && Number(pendingData?.distanceKm || 0) > 0;
  const shouldWarnWithoutChallenge =
    shouldShowChallenges &&
    eligibleChallenges.length > 0 &&
    selectedChallengeIds.length === 0 &&
    !fixedChallengeIds.some(Boolean);
  const showChallengeWarning = shouldWarnWithoutChallenge && hasAttemptedSaveWithoutChallenge;

  return (
    <Form
      onSubmit={handleReview}
      className="anim-card relative m-auto w-[min(calc(100vw-2rem),28rem)] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-3xl bg-slate-100 px-4 py-4 text-slate-950 shadow-2xl"
    >
      <button
        type="button"
        className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-lg font-bold text-slate-500 shadow-sm hover:bg-white"
        onClick={closeActivityForm}
        aria-label="Close activity form"
      >
        ✕
      </button>
      <h1 className={`${subtitle ? "mb-1" : "mb-4"} pr-8 text-center text-2xl font-extrabold leading-none text-black sm:text-3xl`}>{title}</h1>
      {!!subtitle && <p className="mb-4 mt-2 text-center text-sm text-slate-500">{subtitle}</p>}

      {!showConfirmation && (
        <>
          <div
            className={`mb-4 grid rounded-full border-2 bg-white p-1 shadow-sm ${modeOptions.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}
            role="group"
            aria-label="Choose your activity type"
            style={{ borderColor: colors.blue }}
          >
            {modeOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`min-h-10 rounded-full px-3 text-base font-bold transition ${
                  entryMode === option.key ? "text-white shadow-sm" : "bg-transparent hover:bg-slate-50"
                }`}
                style={entryMode === option.key ? { backgroundColor: colors.blue } : { color: colors.blue }}
                onClick={() => changeMode(option.key)}
                aria-pressed={entryMode === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-lg">
            <Form.Label htmlFor="manualActivityDate" className="mb-1 cursor-pointer text-base font-bold text-black">
              Date of Activity
            </Form.Label>
            <Form.Control
              className={`${inputClassName} mb-3 cursor-pointer`}
              id="manualActivityDate"
              type="date"
              min={inputMinDateString}
              max={inputMaxDateString}
              value={activityDate}
              onChange={(event) => {
                setActivityDate(event.target.value);
                setSelectedChallengeIds([]);
                clearErrors();
              }}
              required
            />

            {showActivityName && entryMode !== MANUAL_ACTIVITY_MODES.STEPS && (
              <>
                <Form.Label htmlFor="manualActivityName" className="mb-1 text-base font-bold text-black">
                  Activity Name
                </Form.Label>
                <Form.Control
                  className={`${inputClassName} mb-3`}
                  id="manualActivityName"
                  type="text"
                  value={fields.activityName}
                  onChange={(event) => updateField("activityName", event.target.value)}
                  placeholder="Walk, run, hike..."
                  maxLength={60}
                  required={requireActivityName}
                />
              </>
            )}

            {entryMode === MANUAL_ACTIVITY_MODES.DISTANCE && (
              <>
                <Form.Label htmlFor="manualActivityDistance" className="mb-1 text-base font-bold text-black">
                  Distance (km)
                </Form.Label>
                <Form.Control
                  className={`${inputClassName} mb-3`}
                  id="manualActivityDistance"
                  type="number"
                  step="0.01"
                  min="0"
                  value={fields.distance}
                  onChange={(event) => updateField("distance", event.target.value)}
                  placeholder={allowTimeOnlyInDistanceMode ? "Distance in km (optional)" : "Your distance in km"}
                  required={!allowTimeOnlyInDistanceMode}
                />
              </>
            )}

            {entryMode === MANUAL_ACTIVITY_MODES.STEPS && (
              <>
                <Form.Label htmlFor="manualActivitySteps" className="mb-1 text-base font-bold text-black">
                  Daily Steps
                </Form.Label>
                <Form.Control
                  className={inputClassName}
                  id="manualActivitySteps"
                  type="number"
                  step="1"
                  min="1"
                  inputMode="numeric"
                  value={fields.steps}
                  onChange={(event) => updateField("steps", event.target.value)}
                  placeholder="Your steps for the day"
                  required
                />
                {Number(fields.steps) > 0 && (
                  <p className="mb-3 mt-2 text-sm text-slate-500">
                    {formatSteps(fields.steps)} steps will count as {formatDistanceKm(getStepsDistanceKm(fields.steps))} km.
                  </p>
                )}
              </>
            )}

            {entryMode !== MANUAL_ACTIVITY_MODES.STEPS && (
              <>
                <Form.Label className="mb-1 text-base font-bold text-black">Duration</Form.Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["hours", "Hours (hh)", 24],
                    ["minutes", "Min. (mm)", 59],
                    ["seconds", "Sec. (ss)", 59],
                  ].map(([key, label, max]) => (
                    <div key={key}>
                      <Form.Control
                        className={inputClassName}
                        aria-label={label}
                        type="number"
                        min="0"
                        max={max}
                        value={fields[key]}
                        onChange={(event) => updateField(key, event.target.value)}
                        onFocus={() => {
                          if (fields[key] === "0") {
                            updateField(key, "");
                          }
                        }}
                        onBlur={() => {
                          if (fields[key] === "") {
                            updateField(key, "0");
                          }
                        }}
                        required
                      />
                      <p className="mb-0 mt-1 text-center text-xs leading-tight text-slate-500">{label}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {showConfirmation && pendingData && (
        <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-lg">
          <p className="mb-2 text-base font-bold text-black">Please confirm your activity:</p>
          {!!pendingData.activityName && <p className="mb-1">🏷️ Activity: {pendingData.activityName}</p>}
          <p className="mb-1">📅 Date: {pendingData.date}</p>
          {pendingData.mode === MANUAL_ACTIVITY_MODES.STEPS ? (
            <>
              <p className="mb-1">👟 Steps: {formatSteps(pendingData.steps)}</p>
              <p className="mb-1">📏 Counts as {formatDistanceKm(pendingData.distanceKm)} km.</p>
            </>
          ) : pendingData.mode === MANUAL_ACTIVITY_MODES.DISTANCE && pendingData.distanceKm > 0 ? (
            <p className="mb-1">📏 Distance: {formatDistanceKm(pendingData.distanceKm)} km</p>
          ) : null}
          <p className="mb-1">⏱️ Duration: {pendingData.hours}h {pendingData.minutes}m {pendingData.seconds}s</p>

          {shouldShowChallenges && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <p className="mb-1 font-bold text-black">Add these kilometres to active challenges?</p>
              <p className="mb-3 text-sm text-slate-500">Select any challenges that should receive this activity.</p>
              {eligibleChallenges.length > 0 ? (
                <div className="grid gap-2">
                  {eligibleChallenges.map((challenge) => (
                    <label
                      key={challenge.legId}
                      className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 px-3 py-3 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={selectedChallengeIds.includes(challenge.legId)}
                        onChange={() => toggleChallenge(challenge.legId)}
                      />
                      <span className="font-semibold text-slate-800">{getChallengeLabel(challenge)}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="mb-0 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-600">
                  No eligible active challenges are available for this date.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!!activeError && (
        <Alert variant="danger" className="anim-card mt-3 mb-0 py-2">
          {activeError}
        </Alert>
      )}

      {showChallengeWarning && (
        <p
          id="manualActivityChallengeWarning"
          className="mt-3 mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          Are you sure you want to save this activity without adding it to a challenge? If not, select one now
        </p>
      )}

      {showConfirmation ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            className="!min-h-11 !rounded-full"
            variant="secondary"
            type="button"
            onClick={() => {
              setShowConfirmation(false);
              setPendingData(null);
              clearErrors();
            }}
          >
            Edit
          </Button>
          <Button
            className="!min-h-11 !rounded-full !border-none"
            type="button"
            onClick={submitPendingActivity}
            disabled={submissionState !== "idle"}
            aria-describedby={showChallengeWarning ? "manualActivityChallengeWarning" : undefined}
            style={{ backgroundColor: colors.blue }}
          >
            {showChallengeWarning ? "Yes, save" : "Save Activity"}
          </Button>
        </div>
      ) : (
        <Button
          className="mt-3 !min-h-12 w-full !rounded-full !border-none !text-lg !font-bold !shadow-lg"
          type="submit"
          disabled={submissionState !== "idle"}
          style={{ backgroundColor: colors.blue }}
        >
          Review
        </Button>
      )}
      <p className="mx-auto my-3 max-w-sm text-center text-sm leading-snug text-slate-500">
        *Activities use the honour system. We trust that your activity is legitimate. ;)
      </p>
    </Form>
  );
}

export default ManualActivityForm;
